import React from 'react';
import { CheckCircle } from 'lucide-react';
import { cn } from '~/lib/utils';

interface StepDef {
  id: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  [key: string]: any;
}

interface FormStepperProps {
  steps: StepDef[];
  currentStep: number;
  onStepClick?: (index: number) => void;
  allowNavigation?: boolean;
}

export function FormStepper({
  steps,
  currentStep,
  onStepClick,
  allowNavigation = true
}: FormStepperProps) {
  return (
    <div className="w-full">
      {/* Desktop view */}
      <div className="hidden md:flex items-center justify-between">
        {steps.map((step, idx) => {
          const Icon = step.icon;
          const isActive = idx === currentStep;
          const isCompleted = idx < currentStep;
          const isClickable = allowNavigation && isCompleted;

          return (
            <React.Fragment key={step.id}>
              <div
                className={cn(
                  "flex flex-col items-center relative",
                  isClickable && "cursor-pointer"
                )}
                onClick={() => isClickable && onStepClick?.(idx)}
                role={isClickable ? "button" : undefined}
                tabIndex={isClickable ? 0 : undefined}
                onKeyDown={(e) => {
                  if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    onStepClick?.(idx);
                  }
                }}
              >
                <div
                  className={cn(
                    "w-10 h-10 rounded-full flex items-center justify-center mb-2 transition-all duration-200",
                    isActive && "bg-primary text-primary-foreground ring-2 ring-primary ring-offset-2",
                    isCompleted && "bg-green-500 text-white",
                    !isActive && !isCompleted && "bg-muted text-muted-foreground",
                    isClickable && "hover:scale-110"
                  )}
                >
                  {isCompleted ? (
                    <CheckCircle className="h-5 w-5" />
                  ) : Icon ? (
                    <Icon className="h-5 w-5" />
                  ) : (
                    <span className="text-sm font-medium">{idx + 1}</span>
                  )}
                </div>
                <span
                  className={cn(
                    "text-xs text-center max-w-[80px]",
                    isActive && "font-medium text-primary",
                    isCompleted && "text-green-600",
                    !isActive && !isCompleted && "text-muted-foreground"
                  )}
                >
                  {step.label}
                </span>
              </div>
              {idx < steps.length - 1 && (
                <div
                  className={cn(
                    "flex-1 h-0.5 mx-2 transition-colors",
                    idx < currentStep ? "bg-green-500" : "bg-muted"
                  )}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Mobile view - compact */}
      <div className="md:hidden">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium">
            Step {currentStep + 1} of {steps.length}
          </span>
          <span className="text-sm text-muted-foreground">
            {steps[currentStep]?.label}
          </span>
        </div>
        <div className="flex gap-1">
          {steps.map((step, idx) => (
            <div
              key={step.id}
              className={cn(
                "h-1 flex-1 rounded-full transition-colors",
                idx < currentStep && "bg-green-500",
                idx === currentStep && "bg-primary",
                idx > currentStep && "bg-muted"
              )}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default FormStepper;
